import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBan,
  FaBolt,
  FaExternalLinkAlt,
  FaFileSignature,
  FaPaperPlane,
  FaStamp,
  FaSyncAlt,
  FaUserShield,
} from 'react-icons/fa';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { Button, type ButtonSize } from './Button';
import { CancelPolicyModal } from './CancelPolicyModal';
import { RenewPolicyModal } from './RenewPolicyModal';
import { UnderwritingDecisionPanel } from './UnderwritingDecisionPanel';

interface Props {
  policy: Policy;
  size?: ButtonSize;
}

export const WorkflowActions: React.FC<Props> = ({ policy, size = 'md' }) => {
  const navigate = useNavigate();
  const submitToUnderwriting = usePasStore((s) => s.submitToUnderwriting);
  const generateBinder = usePasStore((s) => s.generateBinder);
  const issuePolicy = usePasStore((s) => s.issuePolicy);
  const activatePolicy = usePasStore((s) => s.activatePolicy);

  const [busy, setBusy] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [uwOpen, setUwOpen] = useState(false);

  const run = (key: string, fn: () => Promise<void>) => async () => {
    setBusy(key);
    try {
      await fn();
    } catch {
      // The store surfaces the error as a toast.
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {policy.status === 'draft' && (
          <Button size={size} leftIcon={<FaPaperPlane className="h-3 w-3" />} loading={busy === 'submit'} onClick={run('submit', () => submitToUnderwriting(policy.id))}>
            Submit to Underwriting
          </Button>
        )}

        {policy.status === 'pending_underwriting' && (
          <Button size={size} leftIcon={<FaUserShield className="h-3 w-3" />} onClick={() => setUwOpen(true)}>
            Open Underwriting Review
          </Button>
        )}

        {policy.status === 'approved' && !policy.binderGenerated && (
          <Button size={size} leftIcon={<FaFileSignature className="h-3 w-3" />} loading={busy === 'binder'} onClick={run('binder', () => generateBinder(policy.id))}>
            Generate Binder
          </Button>
        )}
        {policy.status === 'approved' && (
          <Button
            size={size}
            variant={policy.binderGenerated ? 'primary' : 'secondary'}
            leftIcon={<FaStamp className="h-3 w-3" />}
            loading={busy === 'issue'}
            disabled={!policy.binderGenerated}
            title={!policy.binderGenerated ? 'Generate the binder first' : undefined}
            onClick={run('issue', () => issuePolicy(policy.id))}
          >
            Issue Policy
          </Button>
        )}

        {policy.status === 'issued' && (
          <Button size={size} variant="success" leftIcon={<FaBolt className="h-3 w-3" />} loading={busy === 'activate'} onClick={run('activate', () => activatePolicy(policy.id))}>
            Activate Policy
          </Button>
        )}

        {policy.status === 'active' && (
          <>
            <Button size={size} leftIcon={<FaSyncAlt className="h-3 w-3" />} onClick={() => setRenewOpen(true)}>
              Renew
            </Button>
            <Button size={size} variant="secondary" leftIcon={<FaBan className="h-3 w-3" />} onClick={() => setCancelOpen(true)}>
              Cancel Policy
            </Button>
          </>
        )}

        {policy.status === 'renewed' && policy.renewedToId && (
          <Button size={size} variant="secondary" rightIcon={<FaExternalLinkAlt className="h-3 w-3" />} onClick={() => navigate(`/policy-admin/policies/${policy.renewedToId}`)}>
            View Renewal Policy
          </Button>
        )}
      </div>

      {cancelOpen && <CancelPolicyModal isOpen={cancelOpen} onClose={() => setCancelOpen(false)} policy={policy} />}
      {renewOpen && <RenewPolicyModal isOpen={renewOpen} onClose={() => setRenewOpen(false)} policy={policy} />}
      <UnderwritingDecisionPanel policyId={uwOpen ? policy.id : null} isOpen={uwOpen} onClose={() => setUwOpen(false)} />
    </>
  );
};
